import { type BibleViewOptions, parseBibleViewOptions, serializeBibleViewOptions } from '@soulhx/fs-common';
import React, { useCallback, useMemo, useState } from 'react';
import { BibleViewerOptions } from './BibleViewerOptions.js';
import { useViewer, type UseViewerParams } from '../../hooks/useViewer.js';

type IBibleViewer = UseViewerParams & {
  /**
   * Whether the viewing options should be shown
   */
  hideOptions?: boolean;

  /**
   * Optional callback for setting default viewing options
   *
   * @param newVal New setting string
   */
  updateDefaultOptionsCallback?: (newVal: string) => void;

  /**
   * Same as {@link updateDefaultOptionsCallback} except for "local"
   * options instead of global options. (e.g., save to Bookmark but
   * not as default.)
   *
   * Exercise caution if using both callbacks; containing component
   * is responsible for tracking mismatches between what's passed
   * to `options` and how this component will render the options
   * that are shown, whether they're considered "dirty," etc.
   */
  updateLocalDefaultOptionsCallback?: (newVal: string) => void;
};
export const BibleViewer = ({
  version,
  passage,
  options: optionsString,
  updateDefaultOptionsCallback,
  updateLocalDefaultOptionsCallback,
  hideOptions = false,
  readOnly = false,
  showFullPassageControl = false,
  fullCopyright = false,
  forceHideRef = false,
}: IBibleViewer) => {
  const [options, setOptions] = useState(parseBibleViewOptions(optionsString));
  const initialOptions = parseBibleViewOptions(optionsString);

  const updateFunc = useCallback(
    (newOptions: BibleViewOptions) => {
      if (!updateDefaultOptionsCallback) return;

      updateDefaultOptionsCallback(serializeBibleViewOptions(newOptions));
    },
    [updateDefaultOptionsCallback]
  );

  const updateLocalFunc = useCallback(
    (newOptions: BibleViewOptions) => {
      if (!updateLocalDefaultOptionsCallback) return;

      updateLocalDefaultOptionsCallback(serializeBibleViewOptions(newOptions));
    },
    [updateLocalDefaultOptionsCallback]
  );

  const { Viewer } = useViewer({
    passage,
    version,
    options: serializeBibleViewOptions(options),
    readOnly,
    showFullPassageControl,
    fullCopyright,
    forceHideRef,
  });

  const dirtyOptions = useMemo((): boolean => {
    return (
      initialOptions.crossRef !== options.crossRef ||
      initialOptions.footnotes !== options.footnotes ||
      initialOptions.headings !== options.headings ||
      initialOptions.redLetters !== options.redLetters ||
      initialOptions.verseNums !== options.verseNums
    );
  }, [initialOptions, options]);

  return (
    <div className="soulhx-bible-viewer mx-auto" style={{ maxWidth: '750px' }}>
      {!hideOptions && (
        <BibleViewerOptions
          dirtyOptions={dirtyOptions}
          updateDefaults={updateDefaultOptionsCallback ? updateFunc : undefined}
          updateLocalDefaults={updateLocalDefaultOptionsCallback ? updateLocalFunc : undefined}
          options={options}
          update={(newOptions) => setOptions(newOptions)}
        />
      )}

      {Viewer}
    </div>
  );
};

export default BibleViewer;
